<template>
  <div class="d-flex flex-wrap justify-content-end gap-2 mb-2 validation-data-wrapper">
    <!-- Validación de dependencias -->
    <b-badge
      v-if="isLoading.dependencies"
      variant="secondary"
    >
      <b-spinner small />
      Validando dependencias
    </b-badge>
    <b-badge
      v-else
      :variant="validatedDataCounter.total > 0 && counterDependenciesValidated === validatedDataCounter.total ? 'success' : 'danger'"
    >
      Dependencias validadas: {{ counterDependenciesValidated }} / {{ validatedDataCounter.total }}
    </b-badge>

    <!-- Validación de cargos -->
    <b-badge
      v-if="isLoading.positions"
      variant="secondary"
    >
      <b-spinner small />
      Validando puestos
    </b-badge>
    <b-badge
      v-else
      :variant="validatedDataCounter.total > 0 && validatedDataCounter.positions === validatedDataCounter.total ? 'success' : 'danger'"
    >
      Puestos validados: {{ validatedDataCounter.positions }} / {{ validatedDataCounter.total }}
    </b-badge>

    <!-- Validación de grados -->
    <b-badge
      v-if="isLoading.grades"
      variant="secondary"
    >
      <b-spinner small />
      Validando grados
    </b-badge>
    <b-badge
      v-else
      :variant="validatedDataCounter.total > 0 && validatedDataCounter.grades === validatedDataCounter.total ? 'success' : 'danger'"
    >
      Grados validados: {{ validatedDataCounter.grades }} / {{ validatedDataCounter.total }}
    </b-badge>

    <!-- Validación de departamentos -->
    <b-badge
      v-if="isLoading.departments"
      variant="secondary"
    >
      <b-spinner small />
      Validando departamentos
    </b-badge>
    <b-badge
      v-else
      :variant="validatedDataCounter.total > 0 && validatedDataCounter.departments === validatedDataCounter.total ? 'success' : 'danger'"
    >
      Departamentos validados: {{ validatedDataCounter.departments }} / {{ validatedDataCounter.total }}
    </b-badge>

    <!-- Validación de entidades federativas de titulación -->
    <b-badge
      v-if="isLoading.efTitulacion"
      variant="secondary"
    >
      <b-spinner small />
      Validando entidades federativas
    </b-badge>
    <b-badge
      v-else
      :variant="validatedDataCounterOptional.efTitulacion > 0 && validatedDataCounter.efTitulacion === validatedDataCounterOptional.efTitulacion ? 'success' : 'danger'"
    >
      Entidades federativas validadas: {{ validatedDataCounter.efTitulacion }} / {{ validatedDataCounterOptional.efTitulacion }}
    </b-badge>
  </div>
</template>

<script>
import { postData } from '@/@core/utils/http/general'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { BSpinner, BBadge } from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { ref, computed } from '@vue/composition-api'
import store from '@/store'
import { UNKNOWN_IDS } from '../useMassiveRegister'

export default {
  components: {
    BSpinner,
    BBadge,
  },
  directives: {
    Ripple,
  },
  props: {
    excelData: {
      type: Array,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { token } = store.state.cuenta.session
    const isLoading = ref({
      dependencies: false,
      positions: false,
      grades: false,
      departments: false,
      efTitulacion: false,
    })
    const validatedDataCounter = ref({
      total: 0,
      dependencies: 0,
      positions: 0,
      grades: 0,
      departments: 0,
      efTitulacion: 0,
    })
    const validatedDataCounterOptional = ref({
      efTitulacion: 0,
    })
    const toast = useToast()

    const validateDependencies = async () => {
      try {
        isLoading.value.dependencies = true

        const dependencies = props.excelData.map(item => ({
          id: item.curp,
          tipoGobierno: item.tipoGobierno,
          entidadFederativa: item.entidadFederativa,
          municipio: item.municipio,
          dependencia: item.dependencia,
        }))

        validatedDataCounter.value.total = dependencies.length

        const body = new FormData()

        body.append('task', 'wsValidateDependencies')
        body.append('origin', process.env.VUE_APP_ORIGIN_API)
        body.append('token', token)
        body.append('data', JSON.stringify(dependencies))

        const { data } = await postData(body)
        const dependenciesValidated = data.data || []

        // Si no se pudieron validar las dependencias
        if (data.status !== 200) {
          throw new Error(data.message)
        }

        validatedDataCounter.value.dependencies = dependenciesValidated.filter(dep => (dep.id_dependencia && dep.id_dependencia !== UNKNOWN_IDS.dependencia)).length

        emit('dependency-validation-completed', dependenciesValidated)
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Validación de dependencias fallida',
            text: error.message,
            icon: 'XIcon',
            variant: 'danger',
          },
        },
        { timeout: 8000 })
      } finally {
        isLoading.value.dependencies = false
      }
    }

    const validatePosition = async () => {
      try {
        isLoading.value.positions = true

        const positions = props.excelData.map(item => ({
          id: item.curp,
          puesto: item.puesto,
        }))

        const body = new FormData()

        body.append('task', 'wsValidatePositions')
        body.append('origin', process.env.VUE_APP_ORIGIN_API)
        body.append('token', token)
        body.append('data', JSON.stringify(positions))

        const { data } = await postData(body)
        const positionsValidated = data.data || []

        // Si no se pudieron validar los datos
        if (data.status !== 200) {
          throw new Error(data.message)
        }

        validatedDataCounter.value.positions = positionsValidated.filter(pos => (pos.id_puesto && pos.id_puesto !== UNKNOWN_IDS.puesto)).length

        emit('position-validation-completed', positionsValidated)
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Validación de cargos fallida',
            text: error.message,
            icon: 'XIcon',
            variant: 'danger',
          },
        },
        { timeout: 8000 })
      } finally {
        isLoading.value.positions = false
      }
    }

    const validateGrade = async () => {
      try {
        isLoading.value.grades = true

        const grades = props.excelData.map(item => ({
          id: item.curp,
          grado: item.grado,
        }))

        const body = new FormData()

        body.append('task', 'wsValidateGrades')
        body.append('origin', process.env.VUE_APP_ORIGIN_API)
        body.append('token', token)
        body.append('data', JSON.stringify(grades))

        const { data } = await postData(body)
        const gradesValidated = data.data || []

        // Si no se pudieron validar los datos
        if (data.status !== 200) {
          throw new Error(data.message)
        }

        validatedDataCounter.value.grades = gradesValidated.filter(pos => (pos.id_grado && pos.id_grado !== UNKNOWN_IDS.grado)).length

        emit('grade-validation-completed', gradesValidated)
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Validación de grados fallida',
            text: error.message,
            icon: 'XIcon',
            variant: 'danger',
          },
        },
        { timeout: 8000 })
      } finally {
        isLoading.value.grades = false
      }
    }

    const validateDepartment = async () => {
      try {
        isLoading.value.departments = true

        const departments = props.excelData.map(item => ({
          id: item.curp,
          departamento: item.departamento,
        }))

        const body = new FormData()

        body.append('task', 'wsValidateDepartments')
        body.append('origin', process.env.VUE_APP_ORIGIN_API)
        body.append('token', token)
        body.append('data', JSON.stringify(departments))

        const { data } = await postData(body)
        const departmentsValidated = data.data || []

        // Si no se pudieron validar los datos
        if (data.status !== 200) {
          throw new Error(data.message)
        }

        validatedDataCounter.value.departments = departmentsValidated.filter(pos => (pos.id_departamento && pos.id_departamento !== UNKNOWN_IDS.departamento)).length

        emit('department-validation-completed', departmentsValidated)
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Validación de departamentos fallida',
            text: error.message,
            icon: 'XIcon',
            variant: 'danger',
          },
        },
        { timeout: 8000 })
      } finally {
        isLoading.value.departments = false
      }
    }

    const validateEfTitulacion = async () => {
      try {
        isLoading.value.efTitulacion = true

        const ef = props.excelData.map(item => {
          if (!item.efTitulacion) return null
          return {
            id: item.curp,
            ef: item.efTitulacion,
          }
        }).filter(item => item)

        validatedDataCounterOptional.value.efTitulacion = ef.length

        const body = new FormData()

        body.append('task', 'wsValidateEF')
        body.append('origin', process.env.VUE_APP_ORIGIN_API)
        body.append('token', token)
        body.append('data', JSON.stringify(ef))

        const { data } = await postData(body)
        const efValidated = data.data || []

        // Si no se pudieron validar los datos
        if (data.status !== 200) {
          throw new Error(data.message)
        }

        validatedDataCounter.value.efTitulacion = efValidated.filter(pos => (pos.id_entidadFederativa && pos.id_entidadFederativa !== UNKNOWN_IDS.entidadFederativa)).length

        emit('ef-validation-completed', efValidated)
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Validación de entidades federativas fallida',
            text: error.message,
            icon: 'XIcon',
            variant: 'danger',
          },
        },
        { timeout: 8000 })
      } finally {
        isLoading.value.efTitulacion = false
      }
    }

    const counterDependenciesValidated = computed(() => props.excelData.filter(item => item.id_dependencia || item.id_municipio).length)

    return {
      isLoading,
      validatedDataCounter,
      validatedDataCounterOptional,
      counterDependenciesValidated,

      validateDependencies,
      validatePosition,
      validateGrade,
      validateDepartment,
      validateEfTitulacion,
    }
  },
}
</script>

<style lang="scss" scoped>
.validation-data-wrapper {
  gap: .5em;
}
</style>
